import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

/** API responses use `{ success, data }` */
function unwrapData(body) {
  if (body != null && typeof body === "object" && "data" in body) {
    return body.data;
  }
  return body;
}

// Async thunks
export const fetchCourses = createAsyncThunk(
  "course/fetchCourses",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/courses", { params });
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courses"
      );
    }
  }
);

export const fetchCourseById = createAsyncThunk(
  "course/fetchCourseById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/courses/${id}`);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch course"
      );
    }
  }
);

export const createCourse = createAsyncThunk(
  "course/createCourse",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/courses", data);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create course"
      );
    }
  }
);

export const updateCourse = createAsyncThunk(
  "course/updateCourse",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/courses/${id}`, data);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course"
      );
    }
  }
);

export const deleteCourse = createAsyncThunk(
  "course/deleteCourse",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/courses/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete course"
      );
    }
  }
);

export const fetchCourseDays = createAsyncThunk(
  "course/fetchCourseDays",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/courses/${courseId}/days`);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch course days"
      );
    }
  }
);

export const createCourseDay = createAsyncThunk(
  "course/createCourseDay",
  async ({ courseId, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/courses/${courseId}/days`, data);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create course day"
      );
    }
  }
);

export const updateCourseDay = createAsyncThunk(
  "course/updateCourseDay",
  async ({ dayId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/courses/days/${dayId}`, data);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course day"
      );
    }
  }
);

export const deleteCourseDay = createAsyncThunk(
  "course/deleteCourseDay",
  async (dayId, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/courses/days/${dayId}`);
      return dayId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete course day",
      );
    }
  },
);

export const finalizeCourse = createAsyncThunk(
  "course/finalizeCourse",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/courses/${courseId}/finalize`);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to finalize course",
      );
    }
  },
);

export const fetchCourseEnrollments = createAsyncThunk(
  "course/fetchCourseEnrollments",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/courses/${courseId}/enrollments`);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch enrollments"
      );
    }
  }
);

export const fetchCourseAnalytics = createAsyncThunk(
  "course/fetchCourseAnalytics",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/courses/${courseId}/analytics`);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch analytics"
      );
    }
  }
);

const courseSlice = createSlice({
  name: "course",
  initialState: {
    courses: [],
    currentCourse: null,
    courseDays: [],
    enrollments: [],
    analytics: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCourse: (state) => {
      state.currentCourse = null;
      state.courseDays = [];
      state.enrollments = [];
      state.analytics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = action.payload;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCourseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourseById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCourse = action.payload;
      })
      .addCase(fetchCourseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.courses.unshift(action.payload);
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCourse.fulfilled, (state, action) => {
        const index = state.courses.findIndex(
          (course) => course.id === action.payload.id
        );
        if (index !== -1) {
          state.courses[index] = action.payload;
        }
        state.currentCourse = action.payload;
      })
      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.courses = state.courses.filter(
          (course) => course.id !== action.payload
        );
      })
      .addCase(fetchCourseDays.fulfilled, (state, action) => {
        state.courseDays = action.payload;
      })
      .addCase(createCourseDay.fulfilled, (state, action) => {
        state.courseDays.push(action.payload);
      })
      .addCase(updateCourseDay.fulfilled, (state, action) => {
        const index = state.courseDays.findIndex(
          (day) => day.id === action.payload.id,
        );
        if (index !== -1) {
          state.courseDays[index] = action.payload;
        }
      })
      .addCase(deleteCourseDay.fulfilled, (state, action) => {
        state.courseDays = state.courseDays.filter(
          (day) => day.id !== action.payload,
        );
      })
      .addCase(fetchCourseEnrollments.fulfilled, (state, action) => {
        state.enrollments = action.payload;
      })
      .addCase(fetchCourseAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
      });
  },
});

export const { clearError, clearCurrentCourse } = courseSlice.actions;
export default courseSlice.reducer;
